import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useSmsAliasConnector } from '@/features/campaigns/v2/Sms/hooks/useSmsAliasConnector';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { PrepareConnectorFlowStepResponse, useCreateFlowInstanceMutation, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateSenderOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

export const SmsSenderEditFlow: FC = () => {
  const { senderId } = useParams<{ senderId: string }>();
  const { id: workspaceId } = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { editFlowId, connectorInstanceId, isLoading: isConnectorLoading, missingEditFlow } = useSmsAliasConnector();
  const { t } = useTranslation('campaign');
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [formData, setFormData] = useState<FlowFormData>({});
  const [createFlowInstance, { isLoading: isCreatingFlowInstance }] = useCreateFlowInstanceMutation();

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: editFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: senderId ? { alias_id: senderId, sender_id: senderId } : {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      setPrepareResult(translatedResponse?.configuration?.elements?.params as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!editFlowId && !!audienceId) {
      void fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, currentLanguage, editFlowId, prepareStep, senderId, workspaceId]);

  const handleEditSender = async (opts?: CreateSenderOpts) => {
    if (!connectorInstanceId) {
      const message = String(t('smsWorkspace.senders.missingConnectorInstance'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!editFlowId) {
      const message = String(t('smsWorkspace.senders.missingEditFlow'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const flowParams = {
      ...(opts?.flowParams ?? formData),
      ...(senderId ? { alias_id: senderId } : {}),
    };
    const flowMappings = opts?.flowMappings ?? {};

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: editFlowId,
      connectorInstanceId,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(showToast({ title: String(t('smsWorkspace.senders.editSuccess')), type: ToastType.SUCCESS }));
      history.push(RouteConstants.smsSenders);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('smsWorkspace.senders.editFailed'));
      appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
      return { success: false, message: errorMessage };
    }
  };

  const multiStepFlowFormProps = {
    id: 'sms-sender-edit-form',
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: editFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData) => {
      setFormData(allParams);
      void handleEditSender({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
    },
  };

  const isConnectorReady = !!connectorInstanceId && !!editFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;

  if (isAudienceLoading || isConnectorLoading || isPreparePending) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.editTitle')}</h2>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.editTitle')}</h2>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  if (missingEditFlow) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.editTitle')}</h2>
        <p className='text-sm text-gray-600'>{t('smsWorkspace.senders.missingEditFlow')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.editTitle')}</h2>
      <ErrorBoundary>{prepareResult ? <MultiStepFlowForm {...multiStepFlowFormProps} isInAction={isCreatingFlowInstance} /> : null}</ErrorBoundary>
    </div>
  );
};
